import {
  Box,
  Button,
  Heading as ChakraHeading,
  IconButton,
  Image,
  Link,
} from '@chakra-ui/react';
import React from 'react';
import { FiMenu } from 'react-icons/fi';
import logo from '../../images/olympiq-logo.svg';
import Padding from '../padding/Padding';
import { TOP_FEATURES_ITEMS, LEARN_MORE_ITEMS } from './navigationData';
import NavItem from './desktop/NavItem';
import DropdownPanel from './desktop/DropdownPanel';
import MobileDrawer from './mobile/MobileDrawer';

const Heading = (): React.JSX.Element => {
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false);
  const headerRef = React.useRef<HTMLElement>(null);

  const [activeDropdown, setActiveDropdown] = React.useState<'top-features' | 'learn-more' | null>(null);
  const [dropdownPosition, setDropdownPosition] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [headerHeight, setHeaderHeight] = React.useState<number>(0);
  const closeTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  const handleMouseEnter = (dropdown: 'top-features' | 'learn-more', position: { top: number; left: number }) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownPosition(position);
    setActiveDropdown(dropdown);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = window.setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const handleOverlayClick = () => {
    setActiveDropdown(null);
  };

  const getActiveItems = () => {
    if (activeDropdown === 'top-features') return TOP_FEATURES_ITEMS;
    if (activeDropdown === 'learn-more') return LEARN_MORE_ITEMS;
    return [];
  };

  return (
    <Box
      as="header"
      ref={headerRef}
      position="sticky"
      top={0}
      zIndex={100}
      role="banner"
    >
      <Box position="relative" zIndex={101}>
        <Padding verticalPadding={5} backgroundColor="#fffe">
          <Box as="nav" display="flex" alignItems="center" gap={4} aria-label="Main navigation">
            <Link
              href="/"
              display="flex"
              alignItems="center"
              gap={3}
              _hover={{ textDecoration: 'none' }}
              aria-label="Olympiq - Go to homepage"
            >
              <Image className="rounded-lg" src={logo} alt="" width={35} height={35} aria-hidden="true" />
              <ChakraHeading
                as="span"
                style={{
                  fontFamily: 'Bebas Neue',
                  fontSize: '2rem',
                  fontWeight: 'bold',
                  color: '#13131B',
                }}
              >
                Olympiq
              </ChakraHeading>
            </Link>
            <Box flexGrow={1}></Box>
            <Box mr={6} display={{ base: 'none', md: 'flex' }} alignItems="center" gap={8} role="menubar">
              <NavItem
                label="Top Features"
                href="/#overview"
                dropdownKey="top-features"
                isActive={activeDropdown === 'top-features'}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
              <NavItem
                label="Learn More"
                href="/learn-more"
                dropdownKey="learn-more"
                isActive={activeDropdown === 'learn-more'}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              />
            </Box>
            <Button
              style={{ fontFamily: 'lato', fontWeight: 'bold' }}
              display={{ base: 'none', md: 'inline-flex' }}
              backgroundColor="#13131B"
              color="white"
            >
              <a href="/contact-us" style={{ color: 'white' }}>Contact Us</a>
            </Button>
            <IconButton
              aria-label="Open navigation menu"
              variant="ghost"
              onClick={() => setMenuOpen(true)}
              display={{ base: 'inline-flex', md: 'none' }}
              color="#13131B"
            >
              <FiMenu />
            </IconButton>
          </Box>
        </Padding>
      </Box>

      {/* Overlay with blur effect */}
      <Box
        position="fixed"
        top={`${headerHeight}px`}
        left={0}
        right={0}
        bottom={0}
        bg={activeDropdown ? 'rgba(0, 0, 0, 0.4)' : 'transparent'}
        backdropFilter={activeDropdown ? 'blur(8px)' : 'blur(0px)'}
        zIndex={99}
        onClick={handleOverlayClick}
        transition="all 0.35s ease"
        pointerEvents={activeDropdown ? 'auto' : 'none'}
        opacity={activeDropdown ? 1 : 0}
        aria-hidden="true"
      />

      {/* Desktop dropdown panel */}
      {activeDropdown && (
        <DropdownPanel
          items={getActiveItems()}
          onMouseEnter={() => {
            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
              closeTimeoutRef.current = null;
            }
          }}
          onMouseLeave={handleMouseLeave}
          onItemClick={() => setActiveDropdown(null)}
          position={dropdownPosition}
          gradientId={activeDropdown === 'top-features' ? 'desktop-gradient-purple-pink' : 'desktop-gradient-indigo-teal'}
          variant={activeDropdown === 'learn-more' ? 'learn-more' : 'default'}
        />
      )}

      {/* Mobile navigation drawer */}
      <MobileDrawer isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </Box>
  );
};

export default Heading;
